import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { FilePdfBox, Paperclip } from 'mdi-material-ui';
import React, { useEffect, useState } from 'react';

import { api } from 'services/api';
import { extractPdfAttachments } from 'utils/extractPdfAttachments';

type Attachment = { name: string; url: string };

export function XmlAttachmentsButton({
  file,
}: {
  file: { publicId: string; name: string; extension: string };
}) {
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[] | null>(null);
  const [menu, setMenu] = useState<{
    anchorEl: HTMLElement;
    message?: string;
  } | null>(null);

  useEffect(
    () => () => attachments?.forEach(({ url }) => URL.revokeObjectURL(url)),
    [attachments],
  );

  const openAttachment = (attachment: Attachment) =>
    !!window.open(attachment.url, '_blank');

  const showAttachments = (anchorEl: HTMLElement, items: Attachment[]) => {
    if (!items.length) {
      setMenu({ anchorEl, message: 'No attachments' });
      return;
    }

    // Opening after an async download may be blocked by the browser's popup
    // blocker, in which case fall back to the menu (a fresh user click).
    if (items.length === 1 && openAttachment(items[0])) {
      return;
    }

    setMenu({ anchorEl });
  };

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    const anchorEl = event.currentTarget;

    if (loading) {
      return;
    }

    if (file.extension.replace(/^\./, '').toLowerCase() !== 'xml') {
      setMenu({ anchorEl, message: 'No attachments' });
      return;
    }

    if (attachments) {
      showAttachments(anchorEl, attachments);
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.instance.get<string>(
        `/files/${file.publicId}`,
        { responseType: 'text' },
      );

      const items = extractPdfAttachments(data).map(({ name, blob }) => ({
        name,
        url: URL.createObjectURL(blob),
      }));

      setAttachments(items);
      showAttachments(anchorEl, items);
    } catch (error) {
      console.error('Error loading XML attachments:', error);
      setMenu({ anchorEl, message: 'Failed to load attachments' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Attachments">
        <IconButton size="small" onClick={handleClick}>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Paperclip fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menu?.anchorEl}
        open={!!menu}
        onClose={() => setMenu(null)}
        // The menu is portaled, but React still bubbles its events up to the
        // grid cell, which would otherwise hijack arrow-key navigation.
        onKeyDown={(event) => event.stopPropagation()}
      >
        {menu?.message ? (
          <MenuItem disabled>{menu.message}</MenuItem>
        ) : (
          attachments?.map((attachment) => (
            <MenuItem
              key={attachment.url}
              onClick={() => {
                openAttachment(attachment);
                setMenu(null);
              }}
            >
              <ListItemIcon>
                <FilePdfBox fontSize="small" />
              </ListItemIcon>
              <ListItemText>{attachment.name}</ListItemText>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
